import axios from 'axios'

export default class Api {
  static setTokenStrings(response) {
    localStorage.setItem('authHeaderToken', response.data.token)
    localStorage.setItem('authRefreshToken', response.data.refresh_token)
    localStorage.setItem('authHeaderTokenExpiration', new Date(response.data.expiresTimestamp * 1000))
  }

  static removeTokenStrings() {
    localStorage.removeItem('authHeaderToken')
    localStorage.removeItem('authRefreshToken')
    localStorage.removeItem('authHeaderTokenExpiration')
  }

  static getToken() {
    const currentTime = new Date()
    const tokenExpiration = this.getTokenExpiration()

    let tokenString = localStorage.getItem('authHeaderToken')
    if (tokenString === 'undefined') tokenString = undefined

    const token = (currentTime < tokenExpiration) ? tokenString : undefined
    return token
  }

  static getRefreshToken() {
    let refreshTokenString = localStorage.getItem('authRefreshToken')
    if (refreshTokenString === 'undefined') refreshTokenString = undefined

    const refreshToken = refreshTokenString
    return refreshToken
  }

  static getTokenExpiration() {
    let tokenExpirationString = localStorage.getItem('authHeaderTokenExpiration')
    if (tokenExpirationString === 'undefined') tokenExpirationString = undefined

    const tokenExpiration = (tokenExpirationString) ? new Date(tokenExpirationString) : undefined
    return tokenExpiration
  }

  static getTokenRefreshPeriod() {
    const currentTime = new Date()
    const tokenExpiration = this.getTokenExpiration()

    if (tokenExpiration === undefined) return undefined
    if (currentTime >= tokenExpiration) return true // instant user-session refresh()

    const tokenRefreshPeriod = Math.ceil((tokenExpiration.getTime() - currentTime.getTime()) / 2)
    return tokenRefreshPeriod
  }

  static getHeaders(useAuth) {
    let headers = {
      'Access-Control-Allow-Origin': '*'
    }

    if (useAuth && this.getToken()) {
      headers['Authorization'] = `Bearer ${this.getToken()}`
    }

    return headers
  }

  static getBaseUrl() {
    return 'https://api.ridline.com/'
  }

  static addInterceptor(type, url) {
    axios.interceptors.response.use(response => {
      if (response.request.responseURL === url && response.data.token) {
        if (type === 'authentication') {
          this.setTokenStrings(response)
        } else
        if (type === 'registration') {
          this.removeTokenStrings()
          if (response.data.userstatus === 'active') this.setTokenStrings(response)
        } else
        if (type === 'verification') {
          this.removeTokenStrings()
          this.setTokenStrings(response)
        }
      }

      return response
    }, error => {
      return Promise.reject(error)
    })
  }

  static logout() {
    this.removeTokenStrings()
  }

  static login(username, password) {
    const url = this.getBaseUrl() + 'login_check'

    this.addInterceptor('authentication', url)

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(false),
      data: {
        _username: username,
        _password: password
      }
    })
  }

  static refresh() {
    const url = this.getBaseUrl() + 'token/refresh'

    this.addInterceptor('authentication', url)

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(false),
      data: {
        refresh_token: this.getRefreshToken()
      }
    })
  }

  static getUserRequestInfo(joinCode) {
    const url = this.getBaseUrl() + `user/request_info/${joinCode}`

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(false)
    })
  }

  static register(user, requestCode) {
    const url = this.getBaseUrl() + 'user/register'

    this.addInterceptor('registration', url)

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(false),
      data: {
        register: {
          name: user.name,
          email: user.email,
          password: user.password,
          request_code: requestCode
        }
      }
    })
  }

  static verifyUserEmail(verificationCode) {
    const url = this.getBaseUrl() + `user/verifymail/${verificationCode}`

    this.addInterceptor('verification', url)

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(false)
    })
  }

  static getUserData() {
    const url = this.getBaseUrl() + 'user/view'

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(true)
    })
  }

  static searchUsers(email) {
    const url = this.getBaseUrl() + `user/search?search=${email}`

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(true)
    })
  }

  static getNotifications(limit, offset) {
    // const url = this.getBaseUrl() + `user/notifications?limit=${limit}&offset=${offset}`
    const url = this.getBaseUrl() + `user/notifications?limit=6&offset=0`

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(true)
    })
  }

  static getDashboard() {
    const url = this.getBaseUrl() + 'dashboard/'

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(true)
    })
  }

  static getRiderList() {
    const url = this.getBaseUrl() + 'rider/list'

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(true)
    })
  }

  static createRider(title) {
    const url = this.getBaseUrl() + 'rider/create'

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          title: title
        }
      }
    })
  }

  static viewRider(id) {
    const url = this.getBaseUrl() + 'rider/view/' + id

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(true)
    })
  }

  static editRider(id, data) {
    const url = this.getBaseUrl() + 'rider/edit/' + id

    return axios({
      url: url,
      method: 'patch',
      headers: this.getHeaders(true),
      data: {
        edit: {
          title: data.title,
          description: data.description,
          artist: data.artist,
          public: data.publicSharing,
          link_sharing: data.linkSharing
        }
      }
    })
  }

  static shareRider(riderId, email) {
    const url = this.getBaseUrl() + 'rider/share/' + riderId

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        share: {
          email: email,
          role: 'write'
        }
      }
    })
  }

  static removeRiderShare(shareId) {
    const url = this.getBaseUrl() + 'rider/share/' + shareId

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static deleteRider(riderId) {
    const url = this.getBaseUrl() + 'rider/delete/' + riderId

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static moveRiderColumn(column) {
    const url = this.getBaseUrl() + `rider/column/move/${column.id}/${column.position}`

    return axios({
      url: url,
      method: 'patch',
      headers: this.getHeaders(true)
    })
  }

  static deleteRiderColumn(id) {
    const url = this.getBaseUrl() + 'rider/column/delete/' + id

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static createRiderColumn(riderId, column) {
    const url = this.getBaseUrl() + `rider/column/create/${riderId}`

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          title: column.title,
          type: column.type,
          position: column.position
        }
      }
    })
  }

  static createRiderContact(riderId, contact) {
    const url = this.getBaseUrl() + 'rider/contact/create/' + riderId

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          title: contact.title,
          name: contact.name,
          description: 'Please add key responsibilities',
          email: contact.email,
          phone: contact.phone
        }
      }
    })
  }

  static editRiderContact(contact) {
    const url = this.getBaseUrl() + 'rider/contact/edit/' + contact.id

    return axios({
      url: url,
      method: 'patch',
      headers: this.getHeaders(true),
      data: {
        edit: {
          title: contact.title,
          name: contact.name,
          description: contact.description,
          email: contact.email,
          phone: contact.phone
        }
      }
    })
  }

  static deleteRiderContact(contact) {
    const url = this.getBaseUrl() + 'rider/contact/delete/' + contact.id

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static createRiderMember(riderId, member) {
    const url = this.getBaseUrl() + 'rider/member/create/' + riderId

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          name: member.name,
          email: member.email,
          phone: member.phone,
          items: member.items
        }
      }
    })
  }

  static editRiderMember(member) {
    const url = this.getBaseUrl() + 'rider/member/edit/' + member.id

    return axios({
      url: url,
      method: 'patch',
      headers: this.getHeaders(true),
      data: {
        edit: {
          name: member.name,
          email: member.email,
          phone: member.phone,
          items: member.items
        }
      }
    })
  }

  static moveRiderMember(id, position) {
    const url = this.getBaseUrl() + `rider/member/move/${id}/${position}`

    return axios({
      url: url,
      method: 'patch',
      headers: this.getHeaders(true)
    })
  }

  static deleteRiderMember(member) {
    const url = this.getBaseUrl() + 'rider/member/delete/' + member.id

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static createRiderItem(riderId, item) {
    const url = this.getBaseUrl() + 'rider/item/create/' + riderId

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          type: item.type,
          group: item.group,
          class: item.class,
          position: item.position,
          value: item.value,
          title: item.title,
          content: item.content,
          parent: item.parent,
          relation: item.relation,
          params: item.params,
          member: item.member
        }
      }
    })
  }

  static createRiderItems(riderId, parent, items) {
    const url = this.getBaseUrl() + 'rider/item/create_multi/' + riderId

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create_multi: {
          parent: parent,
          items: items
        }
      }
    })
  }

  static cloneRiderItem(riderId, {parent_create, parent, clone_item, clone_count, delete_cloned}) {
    const url = this.getBaseUrl() + 'rider/item/clone/' + riderId

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        clone_item: {
          parent_create,
          parent,
          clone_item,
          clone_count,
          delete_cloned
        }
      }
    })
  }

  static editRiderItem(item) {
    const url = this.getBaseUrl() + 'rider/item/edit/' + item.id

    return axios({
      url: url,
      method: 'patch',
      headers: this.getHeaders(true),
      data: {
        edit: {
          type: item.type,
          group: item.group,
          class: item.class,
          position: item.position,
          value: item.value,
          title: item.title,
          content: item.content,
          params: item.params,
          parent: item.parent,
          relation: item.relation,
          required: item.required,
          member: item.member
        }
      }
    })
  }

  static moveRiderItem(id, position) {
    const url = this.getBaseUrl() + `rider/item/move/${id}/${position}`

    return axios({
      url: url,
      method: 'patch',
      headers: this.getHeaders(true)
    })
  }

  static deleteRiderItem(id) {
    const url = this.getBaseUrl() + 'rider/item/delete/' + id

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static createRiderStageplan(riderId, stageplan) {
    const url = this.getBaseUrl() + 'rider/stageplan/create/' + riderId

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          title: stageplan.title,
          description: stageplan.description,
          width: stageplan.width,
          height: stageplan.height
        }
      }
    })
  }

  static editRiderStageplan(stageplan) {
    const url = this.getBaseUrl() + 'rider/stageplan/edit/' + stageplan.id

    return axios({
      url: url,
      method: 'patch',
      headers: this.getHeaders(true),
      data: {
        edit: {
          title: stageplan.title,
          description: stageplan.description,
          width: stageplan.width,
          height: stageplan.height
        }
      }
    })
  }

  static viewRiderStageplan(stageplanId) {
    const url = this.getBaseUrl() + 'rider/stageplan/view/' + stageplanId

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(true)
    })
  }

  static deleteRiderStageplan(stageplanId) {
    const url = this.getBaseUrl() + 'rider/stageplan/delete/' + stageplanId

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static createRiderStageplanItem(stageplanId, item) {
    const url = this.getBaseUrl() + 'rider/stageplan/item/create/' + stageplanId + '/' + item.type

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          top: item.top,
          left: item.left,
          width: item.width,
          height: item.height,
          rotation: item.rotation,
          icon: item.icon,
          title: item.title, // type === 'custom'
          description: item.description, // type === 'custom'
          rider_item: item.riderItemId, // type === 'rider_item'
          rider_member: item.riderMemberId, // type === 'rider_member'
          params: item.params
        }
      }
    })
  }

  static editRiderStageplanItem(item) {
    const url = this.getBaseUrl() + 'rider/stageplan/item/edit/' + item.id

    return axios({
      url: url,
      method: 'patch',
      headers: this.getHeaders(true),
      data: {
        edit: {
          top: item.top,
          left: item.left,
          width: item.width,
          height: item.height,
          rotation: item.rotation,
          icon: item.icon,
          title: item.title, // type === 'custom'
          description: item.description, // type === 'custom'
          rider_item: item.rider_item && item.rider_item.id, // type === 'rider_item'
          rider_member: item.rider_member, // type === 'rider_member'
          params: item.params
        }
      }
    })
  }

  static deleteRiderStageplanItem(itemId) {
    const url = this.getBaseUrl() + 'rider/stageplan/item/delete/' + itemId

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static getStageElements() {
    const url = this.getBaseUrl() + 'stage_element/list'

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(true)
    })
  }

  static createStageElement(data) {
    const url = this.getBaseUrl() + 'stage_element/create'

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          title: data.title,
          group: data.group
        }
      }
    })
  }

  static getEventList() {
    const url = this.getBaseUrl() + 'event/list'

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(true)
    })
  }

  static viewEvent(id) {
    const url = this.getBaseUrl() + 'event/view/' + id

    return axios({
      url: url,
      method: 'get',
      headers: this.getHeaders(true)
    })
  }

  static createEvent(event) {
    const url = this.getBaseUrl() + 'event/create'

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          title: event.title,
          start: event.start,
          end: event.end
        }
      }
    })
  }

  static editEvent(eventId, data) {
    const url = this.getBaseUrl() + `event/edit/${eventId}`

    return axios({
      url: url,
      method: 'patch',
      headers: this.getHeaders(true),
      data: {
        edit: {
          title: data.title
        }
      }
    })
  }

  static deleteEvent(eventId) {
    const url = this.getBaseUrl() + `event/delete/${eventId}`

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static deleteEventStage(stageId) {
    const url = this.getBaseUrl() + `event/stage/delete/${stageId}`

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static createEventStage(eventId, stage) {
    const url = this.getBaseUrl() + 'event/stage/create/' + eventId

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          title: stage.title
        }
      }
    })
  }

  static createEventUser(eventId, user) {
    const url = this.getBaseUrl() + `event/user/create/${eventId}`

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          email: user.email,
          role: user.role
        }
      }
    })
  }

  static deleteEventUser(eventId, userId) {
    const url = this.getBaseUrl() + `event/user/delete/${eventId}/${userId}`

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }

  static createRiderRequest(eventId, request) {
    const url = this.getBaseUrl() + `event/rider_request/create/${eventId}`

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        create: {
          email: request.email,
          title: request.title,
          description: request.description,
          stage: request.stage
        }
      }
    })
  }

  static fulfillRiderRequest(requestId, riderId) {
    const url = this.getBaseUrl() + `event/rider_request/fulfill/${requestId}`

    return axios({
      url: url,
      method: 'post',
      headers: this.getHeaders(true),
      data: {
        fulfill: {
          rider: riderId
        }
      }
    })
  }

  static deleteRiderRequest(requestId) {
    const url = this.getBaseUrl() + `event/rider_request/delete/${requestId}`

    return axios({
      url: url,
      method: 'delete',
      headers: this.getHeaders(true)
    })
  }
}



// WEBPACK FOOTER //
// ./src/Api.js